import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface AwarenessRow {
  brand: string;
  platform: string;
  label: string;
  reach: number;
  impressions: number;
  followers: number;
  saves: number;
  linkClicks: number;
  profileVisits: number;
  engagementRate: number | null;
  hasData: boolean;
}
interface LeadGen {
  leads: number;
  dealsClosed: number;
  closeRate: number | null;
  leadTargetMonthly: number;
  closeTarget: number;
}
interface ExecResponse {
  fiscalYearLabel: string;
  awareness: AwarenessRow[];
  leadGen: { ig: LeadGen; tt: LeadGen };
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const fmtPct = (n: number | null) => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function ExecSummary() {
  const [data, setData] = useState<ExecResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch("/api/marketing-suite/exec-summary", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load the executive summary.
        </CardContent>
      </Card>
    );
  }
  if (!data) return <Skeleton className="h-96 w-full" />;

  const { ig, tt } = data.leadGen;
  const totalLeads = ig.leads + tt.leads;
  const totalDeals = ig.dealsClosed + tt.dealsClosed;

  const AWARENESS_COLS: { key: keyof AwarenessRow; label: string; pct?: boolean }[] = [
    { key: "reach", label: "Reach" },
    { key: "impressions", label: "Views" },
    { key: "followers", label: "Net New Followers" },
    { key: "saves", label: "Saves" },
    { key: "linkClicks", label: "Link Clicks" },
    { key: "profileVisits", label: "Profile Visits" },
    { key: "engagementRate", label: "Avg Engagement", pct: true },
  ];

  return (
    <div className="space-y-6">
      {/* Lead generation */}
      <Card>
        <CardHeader>
          <CardTitle>🎯 Lead Generation — Faithful Platform ({data.fiscalYearLabel} YTD)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile
              label="Instagram Leads"
              value={fmtInt(ig.leads)}
              sub={`Target ${ig.leadTargetMonthly}/mo`}
            />
            <StatTile
              label="TikTok Leads"
              value={fmtInt(tt.leads)}
              sub={`Target ${tt.leadTargetMonthly}/mo`}
            />
            <StatTile label="Total Social Leads" value={fmtInt(totalLeads)} />
            <StatTile
              label="IG Close Rate"
              value={fmtPct(ig.closeRate)}
              sub={`Target ${fmtPct(ig.closeTarget)}`}
            />
            <StatTile
              label="Deals Closed via Social"
              value={fmtInt(totalDeals)}
              sub={`TT close ${fmtPct(tt.closeRate)}`}
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Leads &amp; deals from HubSpot (social sources). Social leads are cold discovery leads —
            they close lower than referrals, at $0 paid spend.
          </p>
        </CardContent>
      </Card>

      {/* Brand awareness */}
      <Card>
        <CardHeader>
          <CardTitle>📣 Brand Awareness — All Brands ({data.fiscalYearLabel} YTD)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Account
                  </th>
                  {AWARENESS_COLS.map((c) => (
                    <th
                      key={c.key}
                      className="text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap"
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.awareness.map((a) => (
                  <tr
                    key={`${a.brand}-${a.platform}`}
                    className={cn("border-b last:border-0", !a.hasData && "opacity-40")}
                  >
                    <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                      {a.label}
                    </td>
                    {AWARENESS_COLS.map((c) => (
                      <td key={c.key} className="text-right py-2 px-3 tabular-nums">
                        {c.pct
                          ? fmtPct(a.engagementRate)
                          : a.hasData
                            ? fmtInt(a[c.key] as number)
                            : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Faithful Platform Instagram auto-syncs; other accounts populate as their numbers are
            entered or connected.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
